import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import FEATURES from './featureFlags';
import SplashScreen from './components/SplashScreen';
import OnboardingCards from './components/OnboardingCards';
import PushPrompt from './components/PushPrompt';
import PushBanner from './components/PushBanner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ProfileSetup from './pages/ProfileSetup';
import ProfileEdit from './pages/ProfileEdit';
import Social from './pages/Social';
import Discover from './pages/Discover';
import Scores from './pages/Scores';
import Messages from './pages/Messages';
import MessagesList from './pages/MessagesList';
import Settings from './pages/Settings';
import './App.css';

// New pages
import UserSearch from './pages/UserSearch';
import UserProfile from './pages/UserProfile';
import Betting from './pages/Betting';

// Admin pages (separate portal)
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Conditional imports for feature-flagged pages
const Fitness = FEATURES.FITNESS ? require('./pages/Fitness').default : null;
const Play = FEATURES.GPS_PLAY ? require('./pages/Play').default : null;
const GolfUpdates = FEATURES.TOUR_UPDATES ? require('./pages/GolfUpdates').default : null;
const Courses = require('./pages/Courses').default;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <img src="/logo-the-club.png" alt="The Club" className="h-16 w-auto animate-pulse" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <img src="/logo-the-club.png" alt="The Club" className="h-16 w-auto animate-pulse" />
      </div>
    );
  }
  if (isAuthenticated) {
    // New users go to profile setup, returning users go to social
    return <Navigate to={user?.profile_complete ? "/social" : "/profile/setup"} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path="/profile/setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><Social /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
      <Route path="/scores" element={<ProtectedRoute><Scores /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesList /></ProtectedRoute>} />
      <Route path="/messages/:matchId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><UserSearch /></ProtectedRoute>} />
      <Route path="/user/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
      <Route path="/betting" element={<ProtectedRoute><Betting /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Feature-flagged routes */}
      {FEATURES.FITNESS && Fitness && <Route path="/fitness" element={<ProtectedRoute><Fitness /></ProtectedRoute>} />}
      {FEATURES.GPS_PLAY && Play && <Route path="/play" element={<ProtectedRoute><Play /></ProtectedRoute>} />}
      {FEATURES.TOUR_UPDATES && GolfUpdates && <Route path="/updates" element={<ProtectedRoute><GolfUpdates /></ProtectedRoute>} />}

      {/* Admin Portal (completely separate) */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />

      {/* Redirects */}
      <Route path="/matches" element={<Navigate to="/social" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [splashDone, setSplashDone] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(
    () => localStorage.getItem('club_onboarding_done') === 'true'
  );
  const [pushPromptDismissed, setPushPromptDismissed] = useState(false);

  const handleSplashComplete = useCallback(() => setSplashDone(true), []);
  const handleOnboardingComplete = useCallback(() => setOnboardingDone(true), []);
  const handlePushPromptComplete = useCallback(() => {
    setPushPromptDismissed(true);
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          {!splashDone && <SplashScreen onComplete={handleSplashComplete} />}
          <AppRoutes />
          {splashDone && !onboardingDone && (
            <OnboardingGate onComplete={handleOnboardingComplete} />
          )}
          {splashDone && onboardingDone && !pushPromptDismissed && (
            <PushPromptGate onComplete={handlePushPromptComplete} />
          )}
          <PushBanner />
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Don't show onboarding overlay on auth or profile setup pages
function OnboardingGate({ onComplete }) {
  const { isAuthenticated } = useAuth();
  const pathname = window.location.pathname;
  if (!isAuthenticated) return null;
  if (pathname.includes('/login') || pathname.includes('/register') || pathname.includes('/profile/setup') || pathname.includes('/admin') || pathname === '/') {
    return null;
  }
  return <OnboardingCards onComplete={onComplete} />;
}

// Only show push prompt if user is logged in, hasn't enabled notifications,
// and is NOT on profile setup or registration pages (those have their own flow)
function PushPromptGate({ onComplete }) {
  const { isAuthenticated, user } = useAuth();
  const pathname = window.location.pathname;

  // Don't interrupt registration or profile setup
  if (pathname.includes('/register') || pathname.includes('/profile/setup')) {
    return null;
  }
  if (!isAuthenticated || !user) return null;
  if (user.notifications_enabled) {
    onComplete();
    return null;
  }
  // Force re-prompt for users whose subscription was wiped
  // (they'll have notifications_enabled=false but previously dismissed the prompt)
  return <PushPrompt onComplete={onComplete} />;
}

export default App;
